import {
  Component,
  inject,
  ViewChild,
  computed,
  signal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../auth/services/auth.service';
import { HospitalService } from '../../services/hospital.service';
import { Popover } from 'primeng/popover';
import { TutorialService } from '../../services/tutorial.service';
import { Logger } from '../../shared/utils/logger.util';

@Component({
  selector: 'app-profile-dropdown',
  standalone: true,
  imports: [CommonModule, ButtonModule, PopoverModule, TooltipModule],
  template: `
    <div class="profile-wrapper">
      <!-- Profile Button -->
      <p-button
        [style]="{
          background: 'transparent',
          border: 'none',
          color: '#0b435b',
          padding: '8px',
          'border-radius': '6px',
        }"
        (click)="toggleProfile($event)"
        [pTooltip]="'View profile'"
        tooltipPosition="bottom"
      >
        <div class="profile-button-content">
          @if (isHospitalUser()) {
            <div class="avatar-circle avatar-image-container">
              <img src="/assets/icons/Nursing.svg" alt="Hospital user" class="avatar-image" />
            </div>
          } @else {
            <div class="avatar-circle">
              {{ userInitials() }}
            </div>
          }
          <span class="user-name-header">
            {{ userName() }}
          </span>
        </div>
      </p-button>

      <!-- Profile Dropdown Panel -->
      <p-popover
        #profilePanel
        [style]="{
          width: '360px',
        }"
        appendTo="body"
      >
        <div class="profile-dropdown">
          <!-- Header -->
          <div class="profile-header">
            @if (isHospitalUser()) {
              <div class="avatar-large avatar-image-container">
                <img src="/assets/icons/Nursing.svg" alt="Hospital user" class="avatar-image" />
              </div>
            } @else {
              <div class="avatar-large">
                {{ userInitials() }}
              </div>
            }
            <div class="header-info">
              <h3 class="user-full-name">{{ userName() }}</h3>
              <p class="user-email">{{ userEmail() }}</p>
            </div>
          </div>

          <!-- Profile Details -->
          <div class="profile-details">
            <div class="detail-section">
              <h4 class="section-title">Account Information</h4>

              <div class="detail-row">
                <div class="detail-label">
                  <i class="pi pi-user"></i>
                  <span>First Name</span>
                </div>
                <div class="detail-value">
                  {{ userFirstName() || 'Not provided' }}
                </div>
              </div>

              <div class="detail-row">
                <div class="detail-label">
                  <i class="pi pi-user"></i>
                  <span>Last Name</span>
                </div>
                <div class="detail-value">
                  {{ userLastName() || 'Not provided' }}
                </div>
              </div>

              <div class="detail-row">
                <div class="detail-label">
                  <i class="pi pi-at"></i>
                  <span>Email</span>
                </div>
                <div class="detail-value">{{ userEmail() }}</div>
              </div>

              @if (userUsername(); as username) {
                <div class="detail-row">
                  <div class="detail-label">
                    <i class="pi pi-id-card"></i>
                    <span>Username</span>
                  </div>
                  <div class="detail-value">{{ username }}</div>
                </div>
              }

              @if (hospitalName(); as hospital) {
                <div class="detail-row">
                  <div class="detail-label">
                    <i class="pi pi-building"></i>
                    <span>Hospital</span>
                  </div>
                  <div class="detail-value">{{ hospital }}</div>
                </div>
              }
            </div>

            <div class="detail-section">
              <h4 class="section-title">Role & Permissions</h4>

              <div class="detail-row">
                <div class="detail-label">
                  <i class="pi pi-shield"></i>
                  <span>Account Type</span>
                </div>
                <div class="detail-value">
                  <span
                    class="b-pill b-pill-role role-badge"
                    [ngClass]="getRoleBadgeClass()"
                  >
                    {{ getUserRoleDisplay() }}
                  </span>
                </div>
              </div>

              @if (userGroups().length > 0) {
                <div class="detail-row">
                  <div class="detail-label">
                    <i class="pi pi-users"></i>
                    <span>Groups</span>
                  </div>
                  <div class="detail-value">
                    <div class="groups-list">
                      @for (group of userGroups(); track group) {
                        <span class="group-tag">{{
                          formatGroupName(group)
                        }}</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Footer -->
          <div class="profile-footer">
            <button class="tutorial-button" (click)="startTutorial()">
              <i class="pi pi-question-circle"></i>
              <span>Replay Tutorial</span>
            </button>
            <button
              class="settings-button"
              (click)="openNotificationSettings()"
            >
              <i class="pi pi-bell"></i>
              <span>Notification Settings</span>
            </button>
            <button class="logout-button" (click)="handleLogout()">
              <i class="pi pi-sign-out"></i>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </p-popover>
    </div>
  `,
  styles: [
    `
      .profile-wrapper {
        position: relative;
      }

      .profile-button-content {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .avatar-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: linear-gradient(135deg, #0b738e 0%, #0b435b 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        font-family: 'Open Sans', sans-serif;
      }

      .user-name-header {
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: 500;
        color: #0b435b;
      }

      .profile-dropdown {
        display: flex;
        flex-direction: column;
        background: white;
      }

      .profile-header {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        background: linear-gradient(135deg, #0b738e 0%, #0b435b 100%);
        color: white;
      }

      .avatar-large {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 22px;
        font-family: 'Open Sans', sans-serif;
        border: 2px solid rgba(255, 255, 255, 0.3);
      }

      .avatar-image-container {
        background: transparent;
        overflow: hidden;
        border: none;
      }

      .avatar-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }

      .header-info {
        flex: 1;
        min-width: 0;
      }

      .user-full-name {
        font-family: 'Open Sans', sans-serif;
        font-size: 18px;
        font-weight: 700;
        color: white;
        margin: 0 0 4px 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-email {
        font-family: 'Open Sans', sans-serif;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.9);
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .profile-details {
        padding: 20px;
        max-height: 400px;
        overflow-y: auto;
      }

      .detail-section {
        margin-bottom: 24px;
      }

      .detail-section:last-child {
        margin-bottom: 0;
      }

      .section-title {
        font-family: 'Open Sans', sans-serif;
        font-size: 12px;
        font-weight: 700;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin: 0 0 12px 0;
      }

      .detail-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 12px 0;
        border-bottom: 1px solid #f1f5f9;
      }

      .detail-row:last-child {
        border-bottom: none;
      }

      .detail-label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: 'Open Sans', sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #51534a;
        min-width: 120px;
      }

      .detail-label i {
        color: #0b738e;
        font-size: 14px;
      }

      .detail-value {
        font-family: 'Open Sans', sans-serif;
        font-size: 13px;
        color: #0b435b;
        text-align: right;
        word-break: break-word;
      }

      .role-badge {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .groups-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        align-items: flex-end;
      }

      .group-tag {
        display: inline-block;
        padding: 4px 10px;
        background: #f0f9ff;
        color: #0b738e;
        border-radius: 8px;
        font-size: 11px;
        font-weight: 500;
        border: 1px solid #e0f2fe;
      }

      .profile-footer {
        padding: 16px 20px;
        border-top: 1px solid #e5e7eb;
        background: #f8fcfd;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .tutorial-button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: #f0f9ff;
        color: #0b738e;
        border: 1px solid #bae6fd;
        padding: 10px 16px;
        border-radius: 6px;
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .tutorial-button:hover {
        background: #e0f2fe;
        border-color: #7dd3fc;
        color: #0b435b;
      }

      .tutorial-button i {
        font-size: 16px;
      }

      .settings-button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: transparent;
        color: #0b435b;
        border: 1px solid #0b435b;
        padding: 10px 16px;
        border-radius: 6px;
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .settings-button:hover {
        background: #f0f9ff;
        border-color: #0b738e;
        color: #0b738e;
      }

      .settings-button i {
        font-size: 16px;
      }

      .logout-button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: #0b435b;
        color: white;
        border: none;
        padding: 10px 16px;
        border-radius: 6px;
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .logout-button:hover {
        background: #0b738e;
      }

      .logout-button i {
        font-size: 16px;
      }

      /* Custom scrollbar for profile details */
      .profile-details::-webkit-scrollbar {
        width: 6px;
      }

      .profile-details::-webkit-scrollbar-track {
        background: #f1f5f9;
      }

      .profile-details::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
      }

      .profile-details::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .user-name-header {
          display: none;
        }
      }
    `,
  ],
})
export class ProfileDropdownComponent {
  @ViewChild('profilePanel') profilePanel!: Popover;

  public authService = inject(AuthService);
  private router = inject(Router);
  private tutorialService = inject(TutorialService);
  private hospitalService = inject(HospitalService);

  // Computed signals for user data
  readonly currentUser = computed(() => this.authService.currentUser());
  readonly hospitalName = signal<string | null>(null);

  constructor() {
    effect(() => {
      const user = this.currentUser();
      if (user?.hospitalId) {
        this.hospitalService.getById(user.hospitalId).subscribe({
          next: (hospital) => this.hospitalName.set(hospital.name),
          error: (err) => {
            Logger.error('Error fetching hospital:', err);
            this.hospitalName.set(null);
          },
        });
      } else {
        this.hospitalName.set(null);
      }
    });
  }

  readonly userName = computed(() => {
    const user = this.currentUser();
    if (!user) return 'User';
    return (
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.email ||
      'User'
    );
  });

  readonly userInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return 'U';

    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }

    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }

    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }

    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return 'U';
  });

  readonly userEmail = computed(() => this.currentUser()?.email || 'No email');
  readonly userFirstName = computed(() => this.currentUser()?.firstName);
  readonly userLastName = computed(() => this.currentUser()?.lastName);
  readonly userUsername = computed(() => this.currentUser()?.username);
  readonly userGroups = computed(() => this.currentUser()?.groups || []);

  readonly isHospitalUser = computed(() => {
    const groups = this.userGroups();
    return groups.some((g) => g.includes('hospital_users'));
  });

  toggleProfile(event: Event): void {
    this.profilePanel.toggle(event);
  }

  handleLogout(): void {
    this.profilePanel.hide();
    this.authService.logout(false); // Manual logout, not due to expiration
  }

  openNotificationSettings(): void {
    this.profilePanel.hide();
    this.router.navigate(['/settings/notifications']);
  }

  startTutorial(): void {
    this.profilePanel.hide();
    // Delegate to the same service/method used by the header question-mark button
    // so both entry points share identical tutorial behaviour (MYB-4822).
    this.tutorialService.startComprehensiveTutorial();
  }

  getUserRoleDisplay(): string {
    const groups = this.userGroups();

    if (groups.some((g) => g.includes('super_admins'))) {
      return 'Super Admin';
    }
    if (groups.some((g) => g.includes('internal_admins'))) {
      return 'Internal Admin';
    }
    if (groups.some((g) => g.includes('internal_users'))) {
      return 'Internal User';
    }
    if (groups.some((g) => g.includes('hospital_users'))) {
      return 'Hospital User';
    }

    return 'User';
  }

  getRoleBadgeClass(): string {
    const groups = this.userGroups();

    if (groups.some((g) => g.includes('super_admins'))) {
      return 'role-super-admin';
    }
    if (groups.some((g) => g.includes('internal_admins'))) {
      return 'role-admin';
    }
    if (groups.some((g) => g.includes('internal_users'))) {
      return 'role-internal';
    }
    if (groups.some((g) => g.includes('hospital_users'))) {
      return 'role-hospital';
    }

    return '';
  }

  formatGroupName(group: string): string {
    // Extract the last part of the group path and format it
    const groupName = group.split('/').pop() || group;
    return groupName
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
